import { useEffect, useState } from "react";
import {
  Loader2,
  MessageSquare,
  FileText,
  Brain,
  ListTodo,
  Database,
  History,
  Settings,
  ArrowLeft,
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
  Trash2,
  Pencil,
  MessageSquarePlus,
  Check,
} from "lucide-react";
import {
  fetchProject,
  fetchProjectChats,
  updateProject,
  deleteProject,
  pinProject,
  unpinProject,
  archiveProject,
  projectColorHex,
  projectNotFoundMessage,
  isProjectNotFound,
  PROJECT_COLORS,
  PROJECT_ICONS,
  Project,
} from "@/lib/api/projects";
import { updateChat, deleteChat, fetchMe } from "@/lib/api";
import type { Chat } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ProjectFiles } from "@/components/projects/ProjectFiles";
import { ProjectMemory } from "@/components/projects/ProjectMemory";
import { ProjectKnowledge } from "@/components/projects/ProjectKnowledge";
import { ProjectTasks } from "@/components/projects/ProjectTasks";
import { Tip } from "@/components/Tip";

interface Props {
  projectId: string;
  /** Close the project view (back to the previous context). */
  onBack: () => void;
  /** Open a project chat in the main chat view. */
  onOpenChat: (chatId: string) => void;
  /** Refresh the projects list (after delete/pin/archive here). */
  onProjectsChanged: () => void;
  /** Current selection model (for the "new chat" model default). */
  selectedModel: string;
}

type Tab = "overview" | "chats" | "files" | "memory" | "knowledge" | "tasks" | "settings";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Обзор", icon: <History size={15} /> },
  { id: "chats", label: "Чаты", icon: <MessageSquare size={15} /> },
  { id: "files", label: "Файлы", icon: <FileText size={15} /> },
  { id: "memory", label: "Память", icon: <Database size={15} /> },
  { id: "knowledge", label: "База знаний", icon: <Brain size={15} /> },
  { id: "tasks", label: "Задачи", icon: <ListTodo size={15} /> },
  { id: "settings", label: "Настройки", icon: <Settings size={15} /> },
];

/**
 * ProjectPage — full project view (PROJECT_SPEC §3).
 *
 * Implemented now (real backend, Phase 2–3):
 *  - header: icon/name/description, pin, archive, delete (with confirm)
 *  - Overview tab (project metadata — real data)
 *  - Chats tab: project chats from GET /api/projects/{id}/chats
 *    (Phase 3); "new chat" via POST /api/projects/{id}/chats.
 *
 * Phase 5 (real backend): Files tab — project-scoped upload/list/download/
 *  delete on top of user_files (storage-only, no RAG, no mock data).
 * Phase F (real backend): Chats lifecycle (rename/pin/archive/delete,
 *  active/archived views) + Settings tab (name/description/icon/color/
 *  default_model preference + show_project_chats_in_global_chat toggle).
 * Phase 8+ placeholders (NO fake functionality):
 *  - Knowledge, Tasks — disabled/«Скоро» state.
 */
export function ProjectPage({
  projectId,
  onBack,
  onOpenChat,
  onProjectsChanged,
  selectedModel,
}: Props) {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>("overview");

  // Chats tab data (lazy: loaded only when the tab is active).
  const [chats, setChats] = useState<Chat[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsError, setChatsError] = useState<string | null>(null);
  // Archive view: active chats by default; "Архивные" shows archived ones.
  const [showArchivedChats, setShowArchivedChats] = useState(false);
  // Inline rename / delete-confirm for project chats.
  const [chatEditId, setChatEditId] = useState<string | null>(null);
  const [chatEditValue, setChatEditValue] = useState("");
  const [chatDeleteId, setChatDeleteId] = useState<string | null>(null);
  const [chatBusyId, setChatBusyId] = useState<string | null>(null);
  // Global-visibility setting (Settings tab).
  const [showChatsInGlobal, setShowChatsInGlobal] = useState(false);
  const [savingSetting, setSavingSetting] = useState(false);

  // Phase F: editable project settings form (name/description/icon/color/
  // default_model preference). default_model is a PREFERENCE ONLY — it never
  // grants model access (Entitlement always applies, RAIOS Ch.11.6).
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIcon, setFormIcon] = useState(PROJECT_ICONS[0]);
  const [formColor, setFormColor] = useState(PROJECT_COLORS[0].id);
  const [formDefaultModel, setFormDefaultModel] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  // Entitlement-filtered model list for the preference select.
  const [modelOptions, setModelOptions] = useState<{ name: string; display: string }[]>([]);

  // Display names for the preference select (user's available models + the
  // currently stored default, so an unlisted stored value still renders).
  const displayNameOf = (n: string) =>
    modelOptions.find((m) => m.name === n)?.display || n;

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await fetchProject(projectId);
      setProject(p);
      // init the visibility toggle from the loaded project
      setShowChatsInGlobal(Boolean(p.show_project_chats_in_global_chat));
      // init the settings form
      setFormName(p.name);
      setFormDescription(p.description || "");
      setFormIcon(p.icon || PROJECT_ICONS[0]);
      setFormColor(p.color || PROJECT_COLORS[0].id);
      setFormDefaultModel(p.default_model || "");
      setSettingsSaved(false);
    } catch (e) {
      // 404 = foreign or unknown — identical message (anti-enumeration):
      // the UI never reveals whether the project exists.
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось загрузить проект");
    } finally {
      setLoading(false);
    }
  };

  // Model preference options: the user's entitlement-filtered LLM list
  // (same source as the global model selector in ChatPage).
  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((u) => {
        if (cancelled) return;
        const opts = (u.available_models?.llm ?? []).map((name) => ({
          name,
          display: u.model_display_names?.[name] || name,
        }));
        if (project?.default_model && !opts.some((o) => o.name === project.default_model)) {
          opts.push({
            name: project.default_model,
            display: u.model_display_names?.[project.default_model] || project.default_model,
          });
        }
        setModelOptions(opts);
      })
      .catch(() => {
        /* options stay empty — the select still works with the stored value */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.default_model, project?.id]);

  // ---- save project settings (Phase F) ----
  const saveSettings = async () => {
    const name = formName.trim();
    if (!name || savingSettings) return;
    setSavingSettings(true);
    setSettingsSaved(false);
    try {
      const updated = await updateProject(projectId, {
        name,
        description: formDescription.trim() || null,
        icon: formIcon,
        color: formColor,
        default_model: formDefaultModel || null,
      });
      setProject((prev) => (prev ? { ...prev, ...updated } : prev));
      onProjectsChanged();
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch {
      /* keep the form values; the user can retry */
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadChats = async () => {
    setChatsLoading(true);
    setChatsError(null);
    try {
      setChats(await fetchProjectChats(projectId, 50, showArchivedChats));
    } catch (e) {
      setChatsError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось загрузить чаты");
    } finally {
      setChatsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "chats" && !chatsLoading && !chatsError) {
      void loadChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, showArchivedChats]);

  // ---- project chat lifecycle (rename / pin / archive / delete) ----
  const patchChat = async (id: string, data: { title?: string; is_pinned?: boolean; is_archived?: boolean }) => {
    setChatBusyId(id);
    try {
      await updateChat(id, data);
      await loadChats();
      onProjectsChanged();
    } catch {
      /* ignore */
    } finally {
      setChatBusyId(null);
    }
  };
  const renameChatHere = async () => {
    const id = chatEditId;
    const title = chatEditValue.trim();
    setChatEditId(null);
    if (!id || !title) return;
    await patchChat(id, { title });
  };
  const deleteChatHere = async () => {
    const id = chatDeleteId;
    setChatDeleteId(null);
    if (!id) return;
    setChatBusyId(id);
    try {
      await deleteChat(id);
      await loadChats();
      onProjectsChanged();
    } catch {
      /* ignore */
    } finally {
      setChatBusyId(null);
    }
  };

  // ---- global-visibility setting ----
  const toggleGlobalVisibility = async (value: boolean) => {
    setShowChatsInGlobal(value);
    setSavingSetting(true);
    try {
      const updated = await updateProject(projectId, {
        show_project_chats_in_global_chat: value,
      });
      setShowChatsInGlobal(Boolean(updated.show_project_chats_in_global_chat));
      setProject((prev) => (prev ? { ...prev, ...updated } : prev));
      onProjectsChanged();
    } catch {
      setShowChatsInGlobal(!value); // revert on failure
    } finally {
      setSavingSetting(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteProject(projectId);
      onProjectsChanged();
      onBack();
    } catch {
      setBusy(false);
      setDeleteConfirm(false);
    }
  };

  const togglePin = async () => {
    if (!project) return;
    try {
      const updated = project.is_pinned
        ? await unpinProject(projectId)
        : await pinProject(projectId);
      setProject(updated);
      onProjectsChanged();
    } catch {
      /* ignore */
    }
  };

  const doArchive = async () => {
    if (!project) return;
    try {
      await archiveProject(projectId);
      onProjectsChanged();
      onBack();
    } catch {
      /* ignore */
    }
  };

  // Delegate chat creation to the parent: it calls POST
  // /api/projects/{id}/chats and opens the chat in the main chat view
  // (single chat pipeline — no duplicated implementation here).
  const createChatHere = () => {
    onOpenChat(`__new_in_${projectId}`);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-tertiary">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
        <p className="text-sm text-secondary">{error || "Проект не найден"}</p>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
        >
          <ArrowLeft size={15} /> К чатам
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-app">
      {/* Header */}
      <div className="shrink-0 border-b border-hairline px-4 md:px-6 pt-4 pb-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-1.5 rounded-lg text-secondary hover:bg-hoverbg hover:text-primary transition-colors"
            aria-label="Назад"
          >
            <ArrowLeft size={16} />
          </button>
          <span
            className="w-6 h-6 flex items-center justify-center text-lg leading-none"
            style={{ color: projectColorHex(project.color) }}
            aria-hidden
          >
            {project.icon || "◆"}
          </span>
          <h1 className="text-lg font-medium text-primary truncate">{project.name}</h1>
          {project.is_pinned && (
            <Pin size={13} className="shrink-0 text-accent" aria-label="Закреплён" />
          )}
          <div className="flex-1" />
          <button
            onClick={() => void togglePin()}
            className="p-2 rounded-lg text-secondary hover:bg-hoverbg hover:text-primary transition-colors"
            aria-label={project.is_pinned ? "Открепить" : "Закрепить"}
            title={project.is_pinned ? "Открепить" : "Закрепить"}
          >
            <Pin size={16} />
          </button>
          <button
            onClick={() => void doArchive()}
            className="p-2 rounded-lg text-secondary hover:bg-hoverbg hover:text-primary transition-colors"
            aria-label="В архив"
            title="В архив"
          >
            <Archive size={16} />
          </button>
          <button
            onClick={() => setDeleteConfirm(true)}
            className="p-2 rounded-lg text-secondary hover:bg-hoverbg hover:text-red-400 transition-colors"
            aria-label="Удалить проект"
            title="Удалить проект"
          >
            <Trash2 size={16} />
          </button>
        </div>
        {project.description && (
          <p className="mt-1 pl-8 text-sm text-secondary">{project.description}</p>
        )}

        {/* Tabs */}
        <div className="mt-3 flex gap-1 overflow-x-auto scrollbar-hide" role="tablist">
          {TABS.map((t) => {
            const disabled = false;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                disabled={disabled}
                onClick={() => setTab(t.id)}
                className={cn(
                  "shrink-0 h-9 px-3 rounded-lg text-sm flex items-center gap-1.5 transition-colors border-b-2 -mb-px",
                  tab === t.id
                    ? "text-accent border-accent"
                    : disabled
                      ? "text-disabled cursor-not-allowed border-transparent"
                      : "text-secondary border-transparent hover:bg-hoverbg hover:text-primary",
                )}
              >
                {t.icon}
                {t.label}
                {disabled && <span className="text-[10px] text-disabled">скоро</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
          {tab === "overview" && (
            <div className="space-y-4">
              <p className="text-sm text-secondary leading-relaxed">
                {project.description || "У этого проекта пока нет описания."}
              </p>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-surface rounded-lg border border-hairline p-3">
                  <dt className="text-xs text-tertiary">Создан</dt>
                  <dd className="mt-1 text-primary">
                    {new Date(project.created_at).toLocaleDateString("ru-RU")}
                  </dd>
                </div>
                <div className="bg-surface rounded-lg border border-hairline p-3">
                  <dt className="text-xs text-tertiary">Обновлён</dt>
                  <dd className="mt-1 text-primary">
                    {new Date(project.updated_at).toLocaleDateString("ru-RU")}
                  </dd>
                </div>
              </dl>
              <p className="text-xs text-tertiary">
                Файлы, база знаний и задачи появятся в следующих версиях.
              </p>
            </div>
          )}

          {tab === "chats" && (
            <div>
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-medium text-primary">Чаты проекта</h2>
                  {/* Active / Archived view switch */}
                  <div className="inline-flex items-center rounded-lg border border-hairline overflow-hidden">
                    <button
                      onClick={() => setShowArchivedChats(false)}
                      className={cn(
                        "h-7 px-2.5 text-[11px] transition-colors",
                        !showArchivedChats ? "bg-hoverbg2 text-primary" : "text-tertiary hover:text-secondary",
                      )}
                    >
                      Активные
                    </button>
                    <button
                      onClick={() => setShowArchivedChats(true)}
                      className={cn(
                        "h-7 px-2.5 text-[11px] transition-colors",
                        showArchivedChats ? "bg-hoverbg2 text-primary" : "text-tertiary hover:text-secondary",
                      )}
                    >
                      Архивные
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => void createChatHere()}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs text-accent hover:bg-hoverbg transition-colors"
                >
                  <MessageSquarePlus size={13} /> Новый чат
                </button>
              </div>
              {chatsLoading ? (
                <div className="py-10 flex justify-center text-tertiary">
                  <Loader2 size={18} className="animate-spin" />
                </div>
              ) : chatsError ? (
                <div className="py-10 text-center text-sm text-tertiary">{chatsError}</div>
              ) : chats.length === 0 ? (
                <div className="py-10 text-center text-sm text-tertiary">
                  {showArchivedChats
                    ? "В этом проекте нет архивных чатов."
                    : "В этом проекте пока нет чатов."}
                </div>
              ) : (
                <ul className="space-y-1">
                  {chats.map((c) => (
                    <li key={c.id}>
                      <div
                        className={cn(
                          "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors",
                          chatBusyId === c.id ? "opacity-60" : "hover:bg-hoverbg",
                        )}
                      >
                        <button
                          onClick={() => onOpenChat(c.id)}
                          className="flex-1 min-w-0 flex items-center gap-2.5 text-left"
                        >
                          {/* Project identity (icon in project color) */}
                          <span
                            className="shrink-0 w-4 h-4 flex items-center justify-center text-[13px] leading-none"
                            style={{ color: projectColorHex(c.project_color) }}
                            aria-hidden
                          >
                            {c.project_icon || "◆"}
                          </span>
                          {chatEditId === c.id ? (
                            <input
                              autoFocus
                              value={chatEditValue}
                              onChange={(e) => setChatEditValue(e.target.value)}
                              onBlur={() => void renameChatHere()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") void renameChatHere();
                                if (e.key === "Escape") setChatEditId(null);
                              }}
                              className="flex-1 min-w-0 bg-hoverbg border border-hairline rounded px-1.5 py-0.5 text-sm text-primary focus:outline-none focus:border-accent"
                            />
                          ) : (
                            <span className={cn("flex-1 min-w-0 truncate text-sm", c.is_archived && !showArchivedChats ? "text-tertiary" : "text-primary")}>
                              {c.title || "Без названия"}
                            </span>
                          )}
                        </button>
                        <span className="text-[11px] text-tertiary shrink-0 hidden sm:block">
                          {new Date(c.created_at).toLocaleDateString("ru-RU", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {c.is_pinned && <Pin size={12} className="shrink-0 text-accent" />}
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Tip label="Переименовать" side="top">
                            <button
                              onClick={() => {
                                setChatEditId(c.id);
                                setChatEditValue(c.title || "");
                              }}
                              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg2 transition-colors"
                            >
                              <Pencil size={13} />
                            </button>
                          </Tip>
                          <Tip label={c.is_pinned ? "Открепить" : "Закрепить"} side="top">
                            <button
                              onClick={() => void patchChat(c.id, { is_pinned: !c.is_pinned })}
                              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg2 transition-colors"
                            >
                              {c.is_pinned ? <PinOff size={13} /> : <Pin size={13} />}
                            </button>
                          </Tip>
                          <Tip label={showArchivedChats ? "Восстановить из архива" : "В архив"} side="top">
                            <button
                              onClick={() =>
                                void patchChat(c.id, {
                                  is_archived: showArchivedChats ? false : true,
                                })
                              }
                              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg2 transition-colors"
                            >
                              {showArchivedChats ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                            </button>
                          </Tip>
                          <Tip label="Удалить" side="top">
                            <button
                              onClick={() => setChatDeleteId(c.id)}
                              className="p-1.5 rounded-lg text-tertiary hover:text-red-400 hover:bg-hoverbg2 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          </Tip>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "files" && (
            <ProjectFiles projectId={projectId} projectLabel={project?.name} />
          )}

          {tab === "memory" && (
            <ProjectMemory projectId={projectId} projectLabel={project?.name} />
          )}

          {tab === "knowledge" && (
            <ProjectKnowledge projectId={projectId} projectLabel={project?.name} />
          )}

          {tab === "tasks" && (
            <ProjectTasks projectId={projectId} projectLabel={project?.name} />
          )}

          {tab === "settings" && (
            <div className="space-y-5">
              <div>
                <h2 className="text-sm font-medium text-primary">Настройки проекта</h2>
              </div>

              {/* Editable settings: name / description / icon / color /
                  default model (preference only) — Phase F. */}
              <div className="rounded-xl border border-hairline bg-surface p-4 space-y-4">
                <div>
                  <label className="block text-xs text-secondary mb-1.5" htmlFor="pj-settings-name">
                    Название
                  </label>
                  <input
                    id="pj-settings-name"
                    value={formName}
                    maxLength={255}
                    onChange={(e) => setFormName(e.target.value)}
                    disabled={savingSettings}
                    className="w-full h-9 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-xs text-secondary mb-1.5" htmlFor="pj-settings-desc">
                    Описание
                  </label>
                  <textarea
                    id="pj-settings-desc"
                    value={formDescription}
                    rows={3}
                    onChange={(e) => setFormDescription(e.target.value)}
                    disabled={savingSettings}
                    placeholder="Краткое описание проекта"
                    className="w-full px-3 py-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent disabled:opacity-60 resize-y"
                  />
                </div>
                <div>
                  <span className="block text-xs text-secondary mb-1.5">Иконка</span>
                  <div className="flex flex-wrap gap-1.5">
                    {PROJECT_ICONS.map((ic) => (
                      <button
                        key={ic}
                        onClick={() => setFormIcon(ic)}
                        disabled={savingSettings}
                        className={cn(
                          "w-8 h-8 rounded-lg border flex items-center justify-center text-[15px] transition-colors",
                          formIcon === ic
                            ? "border-accent bg-hoverbg2 text-accent"
                            : "border-hairline text-secondary hover:text-primary hover:bg-hoverbg",
                        )}
                        aria-label={`Иконка ${ic}`}
                      >
                        {ic}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block text-xs text-secondary mb-1.5">Цвет</span>
                  <div className="flex flex-wrap gap-1.5">
                    {PROJECT_COLORS.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setFormColor(c.id)}
                        disabled={savingSettings}
                        className={cn(
                          "w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-colors",
                          formColor === c.id ? "border-accent" : "border-transparent",
                        )}
                        aria-label={`Цвет ${c.id}`}
                      >
                        <span
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: c.hex }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-secondary mb-1.5" htmlFor="pj-settings-model">
                    Модель по умолчанию
                  </label>
                  <select
                    id="pj-settings-model"
                    value={formDefaultModel}
                    onChange={(e) => setFormDefaultModel(e.target.value)}
                    disabled={savingSettings}
                    className="w-full h-9 px-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent disabled:opacity-60"
                  >
                    <option value="">Не задана (стандартный выбор)</option>
                    {modelOptions.map((m) => (
                      <option key={m.name} value={m.name}>
                        {m.display}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-tertiary leading-relaxed">
                    Только предпочтение при создании чата проекта. Доступ к моделям
                    всегда определяет тариф (Entitlement) — настройка не расширяет доступ.
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => void saveSettings()}
                    disabled={savingSettings || !formName.trim()}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors disabled:opacity-50"
                  >
                    {savingSettings ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Сохранить
                  </button>
                  {settingsSaved && (
                    <span className="text-xs text-secondary">Сохранено</span>
                  )}
                </div>
              </div>

              {/* Global chat visibility toggle */}
              <div className="rounded-xl border border-hairline bg-surface p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <label
                      className="text-sm text-primary flex items-center gap-2 cursor-pointer"
                      title="Показывать чаты проекта в общем списке чатов"
                    >
                      <input
                        type="checkbox"
                        checked={showChatsInGlobal}
                        disabled={savingSetting}
                        onChange={(e) => void toggleGlobalVisibility(e.target.checked)}
                        className="w-4 h-4 accent-[#22d3ee] cursor-pointer"
                      />
                      Показывать чаты проекта в общем списке чатов
                    </label>
                    <p className="mt-1.5 pl-6 text-xs text-secondary leading-relaxed">
                      {showChatsInGlobal
                        ? "Чаты проекта также отображаются в общем списке чатов (в Sidebar). Чаты остаются чатами проекта: сохраняются project_id и метка проекта."
                        : "Если выключено, чаты проекта доступны только внутри проекта и не отображаются в общем списке чатов."}
                    </p>
                  </div>
                  {savingSetting && <Loader2 size={15} className="shrink-0 text-tertiary animate-spin" />}
                </div>
              </div>

              <div className="pt-2 border-t border-hairline">
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={14} /> Удалить проект
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation — in-palette dialog (no native confirm()). */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => !busy && setDeleteConfirm(false)}
            aria-hidden
          />
          <div className="relative w-full max-w-sm rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
            <h3 className="text-base font-medium text-primary">Удалить проект?</h3>
            <p className="mt-2 text-sm text-secondary">
              «{project.name}» будет удалён. Чаты проекта сохранятся как
              обычные (непривязанные) чаты; инструкции и задачи будут удалены.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(false)}
                disabled={busy}
                className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void doDelete()}
                disabled={busy}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors flex items-center gap-1.5"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project chat delete confirmation */}
      {chatDeleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => !chatBusyId && setChatDeleteId(null)}
            aria-hidden
          />
          <div className="relative w-full max-w-sm rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
            <h3 className="text-base font-medium text-primary">Удалить чат?</h3>
            <p className="mt-2 text-sm text-secondary break-words">
              «{chats.find((c) => c.id === chatDeleteId)?.title || "Без названия"}»
              будет удалён вместе со всей историей.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setChatDeleteId(null)}
                disabled={chatBusyId === chatDeleteId}
                className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void deleteChatHere()}
                disabled={chatBusyId === chatDeleteId}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors flex items-center gap-1.5"
              >
                {chatBusyId === chatDeleteId && <Loader2 size={14} className="animate-spin" />}
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
